import { useState } from 'react';
import { X, Mail, Phone, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { apiClient } from '../../api/client';
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@tools-beaver/shared';
import toast from 'react-hot-toast';

type Step = 'choose' | 'email' | 'phone' | 'verify';
type ContactMethod = 'email' | 'phone';

export function AuthModal() {
  const { isAuthModalOpen, authModalMode, closeAuthModal, setUser } = useAppStore();
  const [step, setStep] = useState<Step>('choose');
  const [, setMethod] = useState<ContactMethod>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isAuthModalOpen) return null;

  const handleClose = () => {
    closeAuthModal();
    setStep('choose');
    setEmail('');
    setPhone('');
    setPassword('');
    setVerifyCode('');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const endpoint = authModalMode === 'register' ? '/auth/register' : '/auth/login';
      const { data } = await apiClient.post(endpoint, { email, password });
      localStorage.setItem(AUTH_TOKEN_KEY, data.data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
      setUser(data.data.user);
      toast.success(`Welcome${data.data.user.name ? `, ${data.data.user.name}` : ''}! 🦫`);
      handleClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Something went wrong';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await apiClient.post('/auth/send-verification', { target: phone, type: 'phone' });
      setStep('verify');
      toast.success('Verification code sent!');
    } catch {
      toast.error('Failed to send code. Check the number and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await apiClient.post('/auth/verify', { target: phone, code: verifyCode, type: 'phone' });
      // Register/login after verification
      const { data } = await apiClient.post('/auth/register', { phone });
      localStorage.setItem(AUTH_TOKEN_KEY, data.data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
      setUser(data.data.user);
      toast.success('Welcome to Tools Beaver! 🦫');
      handleClose();
    } catch {
      toast.error('Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-card-hover animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">🦫</span>
              <h2 className="font-display font-extrabold text-xl text-silver-800">
                {authModalMode === 'register' ? 'Join Tools Beaver' : 'Welcome back'}
              </h2>
            </div>
            <p className="text-silver-500 text-sm">
              {authModalMode === 'register'
                ? 'How would you like us to reach you?'
                : 'Sign in to your account'}
            </p>
          </div>
          <button onClick={handleClose}
            className="p-2 text-silver-400 hover:text-silver-600 hover:bg-silver-50 rounded-xl transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Choose method */}
          {step === 'choose' && (
            <div className="space-y-3">
              <button
                onClick={() => { setMethod('email'); setStep('email'); }}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-silver-200
                           hover:border-garden-400 hover:bg-garden-50 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-silver-800 text-sm">Continue with Email</p>
                  <p className="text-silver-400 text-xs">Get a confirmation email</p>
                </div>
              </button>

              <button
                onClick={() => { setMethod('phone'); setStep('phone'); }}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-silver-200
                           hover:border-garden-400 hover:bg-garden-50 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Phone size={20} className="text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-silver-800 text-sm">Continue with Phone</p>
                  <p className="text-silver-400 text-xs">Via SMS or WhatsApp</p>
                </div>
              </button>

              <p className="text-center text-xs text-silver-400 pt-2">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-garden-600 hover:underline">Terms</a>
                {' & '}
                <a href="/privacy" className="text-garden-600 hover:underline">Privacy Policy</a>
              </p>
            </div>
          )}

          {/* Email form */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input"
                required
                autoFocus
              />
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min 8 characters)"
                  className="input pr-10"
                  required
                  minLength={8}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-silver-400 hover:text-silver-600">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button type="submit" disabled={isLoading} className="btn-primary w-full">
                {isLoading ? <Loader2 size={18} className="animate-spin mx-auto" /> :
                  authModalMode === 'register' ? 'Create Account' : 'Sign In'}
              </button>
              <button type="button" onClick={() => setStep('choose')}
                className="w-full text-sm text-silver-400 hover:text-silver-600 transition-colors pt-1">
                ← Back
              </button>
            </form>
          )}

          {/* Phone form */}
          {step === 'phone' && (
            <form onSubmit={handlePhoneSubmit} className="space-y-3">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="input"
                required
                autoFocus
              />
              <button type="submit" disabled={isLoading} className="btn-primary w-full">
                {isLoading ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Send Code'}
              </button>
              <button type="button" onClick={() => setStep('choose')}
                className="w-full text-sm text-silver-400 hover:text-silver-600 transition-colors pt-1">
                ← Back
              </button>
            </form>
          )}

          {/* Verify code */}
          {step === 'verify' && (
            <form onSubmit={handleVerifySubmit} className="space-y-3">
              <p className="text-sm text-silver-500 text-center">
                We sent a 6-digit code to <strong className="text-silver-700">{phone}</strong>
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="input text-center text-2xl tracking-widest font-bold"
                required
                autoFocus
              />
              <button type="submit" disabled={isLoading || verifyCode.length !== 6} className="btn-primary w-full">
                {isLoading ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Verify'}
              </button>
              <button type="button" onClick={() => setStep('phone')}
                className="w-full text-sm text-silver-400 hover:text-silver-600 transition-colors">
                Resend code
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
